import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Send, Loader2, Newspaper, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Newsletter = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/subscribers`, { email, name: name || null });
      setSubscribed(true);
      toast.success("You're subscribed — the next IMR issue will land in your inbox.");
      setName("");
      setEmail("");
    } catch (err) {
      if (err.response?.status === 409) {
        toast.info("That email is already subscribed.");
      } else {
        toast.error("Could not subscribe right now — please try again.");
      }
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-lg bg-white border border-[#D8E2DC] px-4 py-3 text-sm text-[#081C15] placeholder:text-gray-400 focus:ring-2 focus:ring-[#D4AF37] focus:outline-none transition duration-200";

  return (
    <section id="newsletter" data-testid="newsletter-section" className="bg-[#1B4332] text-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="relative"
        >
          <div className="absolute -inset-3 bg-[#D4AF37]/15 rounded-2xl -rotate-2" aria-hidden="true" />
          <img
            src="/imr-newsletter-issue1.jpg"
            alt="IMR Monthly News & Updates — Issue 1 cover"
            data-testid="newsletter-cover"
            className="relative rounded-xl w-full max-w-md mx-auto object-cover shadow-[0_20px_60px_rgba(0,0,0,0.45)]"
          />
          <div className="absolute -top-4 -right-2 sm:right-6 bg-[#D4AF37] text-[#081C15] rounded-lg px-4 py-2 shadow-lg rotate-2" data-testid="newsletter-issue-badge">
            <p className="font-display font-black text-sm leading-tight">ISSUE 1</p>
            <p className="font-display font-bold text-xs leading-tight">March 2027</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
        >
          <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-[#D4AF37] mb-4">Monthly News &amp; Updates</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight" data-testid="newsletter-heading">
            The IMR industry report, in your inbox
          </h2>
          <p className="mt-5 text-base md:text-lg text-[#D8E2DC] leading-relaxed">
            Every month we publish a short report for poultry producers — industry news, barn
            maintenance guidance, biosecurity matters, and upcoming events across B.C.'s
            agricultural community.
          </p>

          <ul className="mt-7 space-y-3 text-sm text-[#D8E2DC]">
            {["Industry news that affects your operation", "Seasonal barn maintenance checklists", "Biosecurity best practices", "Upcoming events & IMR company updates"].map((item, i) => (
              <li key={i} className="flex items-center gap-3">
                <Newspaper size={16} className="text-[#D4AF37] shrink-0" />
                {item}
              </li>
            ))}
          </ul>

          {subscribed ? (
            <div className="mt-9 flex items-center gap-3 rounded-xl bg-white/10 border border-[#D4AF37]/40 p-5" data-testid="newsletter-success">
              <CheckCircle2 size={22} className="text-[#D4AF37] shrink-0" />
              <p className="font-display font-bold">You're on the list — see you in the next issue!</p>
            </div>
          ) : (
            <form onSubmit={subscribe} className="mt-9 rounded-xl bg-white p-6 md:p-7" data-testid="newsletter-form">
              <p className="font-display font-bold text-lg text-[#081C15]">Subscribe free</p>
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <input
                  data-testid="newsletter-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name (optional)"
                  className={inputCls}
                />
                <input
                  type="email"
                  required
                  data-testid="newsletter-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@farm.ca"
                  className={inputCls}
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="newsletter-subscribe-button"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#081C15] text-white px-7 py-3 font-display font-bold text-sm hover:bg-[#1B4332] disabled:opacity-60 transition-colors duration-200"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
                {sending ? "Subscribing..." : "Subscribe to the Newsletter"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
